<script setup lang="ts">
import { avatarText } from '@core/utils/formatters'
import { take, words } from 'lodash'
import { requiredValidator } from '@validators'
import type { ChangeRequestApproval } from '@/models/changeRequest'
import { useSnackbarStore } from '@/store/snackbar'
import type { ApprovalTypeOption, ApprovalStatusOption } from '@/models/changeRequest'
import type { VForm } from 'vuetify/components'

interface IProps {
  requestApproval?: ChangeRequestApproval | null,
  showSelectUsersDialog?: boolean,
  changeRequestLoading?: boolean,
  editApprovalId?: number | null,
  approvalTypeOptions?: ApprovalTypeOption[],
  approvalStatusOptions?: ApprovalStatusOption[],
  hideDeleteBtn?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  requestApproval: null,
  showSelectUsersDialog: false,
  changeRequestLoading: false,
  editApprovalId: null,
  approvalTypeOptions: () => ([]),
  approvalStatusOptions: () => ([]),
  hideDeleteBtn: false,
})

interface Emit {
  (e: 'update:requestApproval', value:  ChangeRequestApproval | null): void
  (e: 'update:showSelectUsersDialog', value:  boolean): void
  (e: 'update:editApprovalId', value:  number | null): void
  (e: 'onCreateChangeRequestApproval'): void
  (e: 'onUpdateChangeRequestApproval'): void
  (e: 'onDeleteChangeRequestApproval'): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const refVForm = ref<VForm>()

const computedApproval = computed({
  // getter
  get: () => props.requestApproval || {},
  // setter
  set: (newRequestApproval: ChangeRequestApproval | null) => {
    emit('update:requestApproval', newRequestApproval) // Emit update to parent when the local Approval is changed
  }
})

// Remove approver from the list
const removeApprover = (approverKey: number) => {
  if (!computedApproval.value?.approvers_info?.length) return
  computedApproval.value.approvers_info.splice(approverKey, 1)
}

const deleteFile = (fileId: number) => {
  if (!computedApproval.value) return
  const currentDeletedFiles = computedApproval.value?.delete_files?.length ? computedApproval.value?.delete_files : []
  computedApproval.value.delete_files = [...currentDeletedFiles, fileId] // Track the deleted file ID
  // Update the file_attachments if it exists
  computedApproval.value.file_attachments = computedApproval.value.file_attachments && Array.isArray(computedApproval.value.file_attachments)
    ? computedApproval.value.file_attachments.filter((file: any) => file?.id !== fileId)
    : []
}

const isViewApproval = computed(() => {
  if (props.requestApproval?.id) {
    return props.editApprovalId !== props.requestApproval?.id
  } else return false
})

const onSubmit = async () => {
  const isValid = await refVForm.value?.validate()
  if (!isValid || !isValid.valid) {
    return
  }

  props.requestApproval?.id ? emit('onUpdateChangeRequestApproval') : emit('onCreateChangeRequestApproval')
}
</script>

<template>
  <div class="change-request-approval-wrapper">
    <h4 class="px-1 pt-2 pb-1">
      {{ requestApproval?.id ? `Approval ${requestApproval?.code}` : 'Start a new approval' }}
      <VBtn
        v-if="isViewApproval"
        size=""
        class="pa-1"
        :disabled="!!editApprovalId"
        @click="emit('update:editApprovalId', requestApproval?.id)"
      >
        <VIcon icon="tabler-pencil" />
      </VBtn>
    </h4>
    <VForm
      ref="refVForm"
      @submit.prevent="onSubmit"
    >
      <VTable class="change-request-approval-table">
        <tbody>
          <tr>
            <td class="px-1 py-1">
              <span>
                Approval Type
              </span>
              <span v-if="!isViewApproval" class="text-primary ml-1">*</span>
            </td>
            <td class="px-1 py-1">
              <span v-if="isViewApproval">
                {{ requestApproval?.approval_type_display }}
              </span>
              <VSelect
                v-else
                v-model="computedApproval.approval_type"
                :disabled="!!changeRequestLoading"
                :items="approvalTypeOptions"
                item-title="display_name"
                item-value="value"
                label="Approval Type"
                :rules="[requiredValidator]"
              />
            </td>
          </tr>
          <tr v-if="requestApproval?.approval_type === 2">
            <td class="px-1 py-1">
              <span>
                Cost Estimate Approval Amount
              </span>
              <span v-if="!isViewApproval" class="text-primary ml-1">*</span>
            </td>
            <td class="px-1 py-1">
              <span v-if="isViewApproval">
                $
                {{ requestApproval?.cost_estimate_approval_amount }}
              </span>
              <VTextField
                v-else
                v-model="computedApproval.cost_estimate_approval_amount"
                :disabled="!!changeRequestLoading"
                label="Cost Estimate Approval Amount"
                type="number"
                :rules="[requiredValidator]"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 pt-2 pb-1">
              <span>
                Description
              </span>
            </td>
            <td class="px-1 pt-2 pb-1">
              <span v-if="isViewApproval">
                {{ requestApproval?.description }}
              </span>
              <VTextarea
                v-else
                v-model="computedApproval.description"
                :disabled="!!changeRequestLoading"
                name="Description"
                label="Description"
                type="text"
                rows="2"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 py-1">
              <span>
                Approvers
              </span>
            </td>
            <td class="px-1 py-1">
              <VAvatar
                v-for="(approver, approverKey) in computedApproval?.approvers_info"
                :key="approverKey"
                rounded
                :size="25"
                color="primary"
                variant="tonal"
                class="ma-1 cursor-pointer"
              >
                <span
                  class="text-sm-center font-weight-medium"
                >
                  {{ avatarText(take(words(approver?.display_name), 2).join(' ')) }}
                </span>
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-click
                  close-on-content-click
                  close-on-back
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <div class="d-flex align-center justify-center px-2">
                    <span>{{ approver?.display_name }}</span>
                    <VBtn
                      v-if="!isViewApproval"
                      size=""
                      class="pa-1"
                      color="light"
                      :disabled="!!changeRequestLoading"
                      @click="removeApprover(approverKey)"
                    >
                      <VIcon color="primary" icon="mdi-minus-circle" />
                    </VBtn>
                  </div>
                </VMenu>
              </VAvatar>
              <VBtn
                v-if="!isViewApproval"
                size=""
                color="light"
                class="border px-1 ma-1"
                :disabled="!!changeRequestLoading"
                @click="emit('update:showSelectUsersDialog', true)"
              >
                <VIcon
                  size="22"
                  icon="mdi-plus"
                />
              </VBtn>
            </td>
          </tr>
          <tr>
            <td class="px-1 py-1">
              <span>
                Status
              </span>
            </td>
            <td class="px-1 py-1">
              <span v-if="isViewApproval">
                {{ requestApproval?.status_display }}
              </span>
              <VSelect
                v-else
                v-model="computedApproval.status"
                :disabled="!!changeRequestLoading"
                :items="approvalStatusOptions"
                item-title="display_name"
                item-value="value"
                label="Status"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 py-1">
              <span>
                Due Date
              </span>
            </td>
            <td class="px-1 py-1">
               <span v-if="isViewApproval">
                {{ requestApproval?.due_date }}
              </span>
              <!-- Use separate disabled AppDateTimePicker due to disabled property limitations -->
              <AppDateTimePicker
                v-else-if="!!changeRequestLoading"
                :model-value="computedApproval?.due_date"
                :config="{ dateFormat: 'Y-m-d' }"
                placeholder="Due Date"
                disabled
              />
              <!-- /Use separate disabled AppDateTimePicker due to disabled property limitations -->
              <AppDateTimePicker
                v-else
                v-model="computedApproval.due_date"
                :config="{ dateFormat: 'Y-m-d' }"
                clearable
                placeholder="Due Date"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 pb-2 pt-1">
              <span
                @click="computedApproval"
              >
                Files
              </span>
            </td>
            <td class="px-1 pb-2 pt-1">
              <VFileInput
                v-if="!isViewApproval"
                v-model="computedApproval.files"
                :disabled="!!changeRequestLoading"
                multiple
                label="Select files..."
              />
              <div v-if="requestApproval?.id" class="file-container mt-1">
                <div v-for="file in requestApproval?.file_attachments" :key="file.id" class="file-item">
                  <div class="file-icon">
                    <VIcon icon="tabler-file-description" size="40" />
                  </div>
                  <div class="action-buttons">
                    <v-btn
                      :disabled="!!changeRequestLoading"
                      size="sm"
                      class="pa-1"
                      :href="file.file"
                      target="_blank"
                    >
                      <VIcon size="">mdi-eye</VIcon>
                    </v-btn>
                    <v-btn
                      v-if="!isViewApproval"
                      :disabled="!!changeRequestLoading"
                      size="sm"
                      class="pa-1"
                      @click.stop="deleteFile(file.id)"
                    >
                      <VIcon size="">mdi-delete</VIcon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </VTable>
      <div
        v-if="!isViewApproval"
        class="d-flex align-center justify-space-between"
      >
        <div>
          <VBtn
            class="ma-1"
            size="small"
            color="success"
            type="submit"
            :disabled="!!changeRequestLoading"
          >
            <VProgressCircular
              v-if="!!changeRequestLoading"
              class="mr-1"
              size="16"
              width="2"
              indeterminate
              color="white"
            />
            {{ requestApproval?.id ? 'Update' : 'Start' }}
          </VBtn>
          <VBtn
            class="ma-1"
            size="small"
            color="secondary"
            :disabled="!!changeRequestLoading"
            @click="emit('update:editApprovalId', null); computedApproval = null;"
          >
            Cancel
          </VBtn>
        </div>
        <VBtn
          v-if="requestApproval?.id && !hideDeleteBtn"
          class="my-1 mr-1 ml-auto"
          size="small"
          color="primary"
          :disabled="!!changeRequestLoading"
          @click="emit('onDeleteChangeRequestApproval', null)"
        >
          Delete
        </VBtn>
      </div>
    </VForm>
  </div>
</template>

<style lang="scss">
.change-request-approval-wrapper {
  border: 1px solid #4d77de;
  border-radius: 25px;
  padding: 10px 20px;
  .change-request-approval-table {
    tr {
      td:first-child {
        width: 30%;
      }
      td {
        height: unset !important;
        border-bottom: none !important;
      }
    }
  }
}
</style>
